/**
 * Flux Keyboard configurator, ported from flux_website's configurator.
 *
 * Reads its steps, variants and prices from the JSON script rendered by
 * sections/flux-configurator.liquid. Adding to cart posts one line for the
 * base kit plus one per fitted module and extra, in a single request, so a
 * build is never half added. Fitted modules share a hidden `_build` property
 * with the base kit so the cart can show them together.
 */

const LAYER_Z = { base: 10, passive: 20, slot: { 1: 30, 4: 40, 2: 50, 3: 60 }, option: 90 };
// Extras can be ordered in any quantity; the cap only guards against typos
const MAX_EXTRA_QUANTITY = 50;

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const handleize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const interpolate = (template, values) =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? '');

const CHEVRON =
  '<svg class="cfg-step__chevron" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4 2l4 4-4 4" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';

class FluxConfigurator extends HTMLElement {
  connectedCallback() {
    const config = this.querySelector('script[data-config]');
    if (!config || this.data) return;

    this.data = JSON.parse(config.textContent);
    this.strings = this.data.strings;
    this.money = new Intl.NumberFormat(this.data.locale, { style: 'currency', currency: this.data.currency });

    this.preview = this.querySelector('[data-preview]');
    this.caption = this.querySelector('[data-preview-caption]');
    this.stepsEl = this.querySelector('[data-steps]');
    this.summaryEl = this.querySelector('[data-summary]');

    this.indexModules();
    this.state = this.defaultState();
    this.openStep = this.stepKeys()[0] ?? null;
    this.status = 'idle';
    this.pending = false;

    this.addEventListener('click', (event) => this.onClick(event));
    this.render();
  }

  // -------------------------------------------------------------- data

  get slotsStep() {
    return this.data.steps.find((step) => step.type === 'slots');
  }

  get colourIndex() {
    return this.data.product.options.indexOf(this.data.product.colourOption);
  }

  indexModules() {
    this.moduleVariants = new Map();
    for (const module of this.slotsStep?.modules ?? []) {
      for (const variant of module.variants) {
        this.moduleVariants.set(variant.id, { module, variant });
      }
    }
    this.extraVariants = new Map();
    for (const step of this.data.steps.filter((s) => s.type === 'extras')) {
      for (const product of step.products) {
        for (const variant of product.variants) {
          this.extraVariants.set(variant.id, { product, variant });
        }
      }
    }
  }

  defaultState() {
    return {
      options: [...this.data.product.defaultOptions],
      slots: Array(this.slotsStep?.count ?? 0).fill(null),
      extras: {},
    };
  }

  /** Accordion keys in display order: one per option, one per slot, one per extras block. */
  stepKeys() {
    return this.data.steps.flatMap((step) =>
      step.type === 'slots'
        ? Array.from({ length: step.count }, (_, i) => `slot:${i + 1}`)
        : [`${step.type}:${step.id}`]
    );
  }

  optionIndex(step) {
    return this.data.product.options.indexOf(step.option);
  }

  optionLabel(step, value) {
    return step.labels?.[value] ?? value;
  }

  baseVariant() {
    return this.data.product.variants.find((variant) =>
      variant.options.every((value, i) => value === this.state.options[i])
    );
  }

  kitColour() {
    return this.colourIndex >= 0 ? this.state.options[this.colourIndex] : null;
  }

  /** Whether some in-stock variant has this value with the other current choices. */
  valueAvailable(index, value) {
    return this.data.product.variants.some(
      (variant) =>
        variant.available &&
        variant.options.every((v, i) => (i === index ? v === value : v === this.state.options[i]))
    );
  }

  // -------------------------------------------------------------- summary

  summarize() {
    const base = this.baseVariant();
    const optionLabels = this.data.steps
      .filter((step) => step.type === 'option')
      .map((step) => this.optionLabel(step, this.state.options[this.optionIndex(step)]));

    const lines = [
      {
        key: 'base',
        label: `${this.data.product.title} · ${optionLabels.join(' · ')}`,
        quantity: 1,
        amount: base?.price ?? null,
        available: !!base?.available,
      },
    ];

    // Fitted modules, one line per module variant with the slots it fills
    const fitted = new Map();
    this.state.slots.forEach((variantId, i) => {
      if (variantId == null) return;
      const entry = fitted.get(variantId) ?? { slots: [] };
      entry.slots.push(i + 1);
      fitted.set(variantId, entry);
    });
    for (const [variantId, { slots }] of fitted) {
      const { module, variant } = this.moduleVariants.get(variantId);
      lines.push({
        key: `module:${variantId}`,
        variantId,
        slots,
        label: `${module.title} (${variant.colour})`,
        quantity: slots.length,
        amount: variant.price * slots.length,
        available: variant.available,
      });
    }

    for (const [id, quantity] of Object.entries(this.state.extras)) {
      if (!quantity) continue;
      const { product, variant } = this.extraVariants.get(Number(id));
      const label = variant.title === product.title ? product.title : `${product.title} (${variant.title})`;
      lines.push({
        key: `extra:${id}`,
        variantId: Number(id),
        label,
        quantity,
        amount: variant.price * quantity,
        available: variant.available,
      });
    }

    const total = lines.reduce((sum, line) => sum + (line.amount ?? 0), 0);
    const purchasable = lines.every((line) => line.amount !== null && line.available);
    return { base, lines, total, purchasable, optionLabels };
  }

  formatMoney(cents) {
    return this.money.format(cents / 100);
  }

  // -------------------------------------------------------------- events

  onClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this.contains(target)) return;
    const { action } = target.dataset;

    if (action === 'toggle-step') {
      this.openStep = this.openStep === target.dataset.step ? null : target.dataset.step;
      this.render(target.dataset.key);
      return;
    }

    if (action === 'add') {
      this.addToCart();
      return;
    }

    if (action === 'option') {
      this.state.options[Number(target.dataset.index)] = target.dataset.value;
    } else if (action === 'slot') {
      const value = target.dataset.variant;
      this.state.slots[Number(target.dataset.slot) - 1] = value ? Number(value) : null;
    } else if (action === 'extra') {
      const id = target.dataset.variant;
      const next = (this.state.extras[id] ?? 0) + Number(target.dataset.delta);
      this.state.extras[id] = Math.max(0, Math.min(MAX_EXTRA_QUANTITY, next));
    }

    this.status = 'idle';
    this.render(target.dataset.key);
  }

  async addToCart() {
    const summary = this.summarize();
    if (!summary.purchasable || this.pending) return;

    const build = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const items = [{ id: summary.base.id, quantity: 1, properties: { _build: build } }];
    for (const line of summary.lines) {
      if (line.key.startsWith('module:')) {
        const slots = line.slots.map((n) => interpolate(this.strings.slot, { number: n })).join(', ');
        items.push({
          id: line.variantId,
          quantity: line.quantity,
          properties: { [this.strings.fittedIn]: slots, _build: build },
        });
      } else if (line.key.startsWith('extra:')) {
        items.push({ id: line.variantId, quantity: line.quantity });
      }
    }

    this.pending = true;
    this.status = 'idle';
    this.errorDetail = null;
    this.renderSummary();

    try {
      const response = await fetch(this.data.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (response.ok) {
        this.status = 'added';
        window.FluxCart?.refreshCount();
      } else {
        const error = await response.json().catch(() => ({}));
        // 422 means Shopify rejected a line, e.g. not enough stock
        this.status = response.status === 422 ? 'unavailable' : 'error';
        this.errorDetail = error.description ?? null;
      }
    } catch {
      this.status = 'error';
      this.errorDetail = null;
    }

    this.pending = false;
    this.renderSummary();
  }

  // -------------------------------------------------------------- render

  render(focusKey) {
    const summary = this.summarize();
    this.renderPreview(summary);
    this.stepsEl.innerHTML = this.data.steps.map((step) => this.renderStep(step)).join('');
    this.renderSummary(summary);

    if (focusKey) {
      this.querySelector(`[data-key="${CSS.escape(focusKey)}"]`)?.focus();
    }
  }

  layers() {
    const { assetBase, baseLayer } = this.data;
    const colour = handleize(this.kitColour());
    const layers = [{ file: baseLayer, z: LAYER_Z.base }];

    for (const step of this.data.steps) {
      if (step.type === 'option' && step.layerPrefix) {
        const value = this.state.options[this.optionIndex(step)];
        layers.push({ file: `${step.layerPrefix}${handleize(value)}.webp`, z: LAYER_Z.option });
      }
    }

    // The 1U filler piece only sits in the frame when every slot is passive
    if (this.state.slots.length && this.state.slots.every((slot) => slot == null)) {
      layers.push({ file: `cfg-passive-1u-${colour}.webp`, z: LAYER_Z.passive });
    }

    this.state.slots.forEach((variantId, i) => {
      const n = i + 1;
      if (variantId == null) {
        layers.push({ file: `cfg-passive-slot-${n}-${colour}.webp`, z: LAYER_Z.passive });
      } else {
        const { module, variant } = this.moduleVariants.get(variantId);
        layers.push({
          file: `cfg-slot-${n}-${module.key}-${handleize(variant.colour)}.webp`,
          z: LAYER_Z.slot[n] ?? LAYER_Z.slot[1],
        });
      }
    });

    return layers.map((layer) => ({ ...layer, src: `${assetBase}${layer.file}` }));
  }

  renderPreview(summary) {
    const wanted = this.layers();
    const existing = new Map([...this.preview.querySelectorAll('img')].map((img) => [img.dataset.layer, img]));

    for (const layer of wanted) {
      let img = existing.get(layer.file);
      if (!img) {
        img = document.createElement('img');
        img.alt = '';
        img.width = 2560;
        img.height = 1440;
        img.decoding = 'async';
        img.dataset.layer = layer.file;
        img.src = layer.src;
        this.preview.append(img);
      }
      img.style.zIndex = layer.z;
      existing.delete(layer.file);
    }
    existing.forEach((img) => img.remove());

    const build = summary.optionLabels.join(' · ');
    this.preview.setAttribute('aria-label', interpolate(this.strings.previewLabel, { build }));
    this.caption.textContent = build;
  }

  renderStep(step) {
    if (step.type === 'slots') {
      return Array.from({ length: step.count }, (_, i) => this.renderSlot(step, i + 1)).join('');
    }

    const key = `${step.type}:${step.id}`;
    const body = step.type === 'option' ? this.renderOptionChoices(step) : this.renderExtras(step);
    const picked = step.type === 'option' ? this.pickedOption(step) : this.pickedExtras(step);
    return this.stepShell(key, step.title, step.description, picked, body);
  }

  stepShell(key, title, description, picked, body) {
    const open = this.openStep === key;
    return `
      <section class="cfg-step">
        <button type="button" class="cfg-step__header" data-action="toggle-step" data-step="${escapeHtml(key)}" data-key="step-${escapeHtml(key)}" aria-expanded="${open}">
          <span class="cfg-step__heading">
            <span class="cfg-step__title">${escapeHtml(title)}</span>
            ${open ? '' : `<span class="cfg-step__picked">${escapeHtml(picked)}</span>`}
          </span>
          ${CHEVRON}
        </button>
        ${
          open
            ? `<div class="cfg-step__body">
                ${description ? `<p class="flux-small">${escapeHtml(description)}</p>` : ''}
                ${body}
              </div>`
            : ''
        }
      </section>`;
  }

  choice({ key, action, data, label, meta, checked, disabled }) {
    const attrs = Object.entries(data)
      .map(([name, value]) => `data-${name}="${escapeHtml(value)}"`)
      .join(' ');
    return `
      <button type="button" role="radio" class="cfg-choice" aria-checked="${checked}" data-action="${action}" data-key="${escapeHtml(key)}" ${attrs} ${disabled ? 'disabled' : ''}>
        <span class="cfg-choice__label">${escapeHtml(label)}</span>
        <span class="cfg-choice__meta">${escapeHtml(meta ?? '')}</span>
      </button>`;
  }

  // Option steps (Layout, Switch, Colour): pick one value of a product option
  renderOptionChoices(step) {
    const index = this.optionIndex(step);
    if (index < 0) return '';
    const values = [...new Set(this.data.product.variants.map((variant) => variant.options[index]))];
    const choices = values
      .map((value) =>
        this.choice({
          key: `option-${index}-${value}`,
          action: 'option',
          data: { index, value },
          label: this.optionLabel(step, value),
          meta: this.valueAvailable(index, value) ? '' : this.strings.outOfStock,
          checked: this.state.options[index] === value,
        })
      )
      .join('');
    return `<div class="cfg-choices" role="radiogroup">${choices}</div>`;
  }

  pickedOption(step) {
    const index = this.optionIndex(step);
    return index < 0 ? '' : this.optionLabel(step, this.state.options[index]);
  }

  // Slot steps: the passive piece, or a module grouped by colour
  renderSlot(step, n) {
    const key = `slot:${n}`;
    const current = this.state.slots[n - 1];
    const title = interpolate(this.strings.slotTitle, { number: n });

    let picked = step.passiveLabel;
    if (current != null) {
      const { module, variant } = this.moduleVariants.get(current);
      picked = `${module.title} (${variant.colour})`;
    }

    const passive = this.choice({
      key: `slot-${n}-passive`,
      action: 'slot',
      data: { slot: n, variant: '' },
      label: step.passiveLabel,
      meta: this.strings.included,
      checked: current == null,
    });

    const colours = [...new Set(step.modules.flatMap((module) => module.variants.map((v) => v.colour)))];
    const groups = colours
      .map((colour) => {
        const choices = step.modules
          .map((module) => {
            const variant = module.variants.find((v) => v.colour === colour);
            if (!variant) return '';
            return this.choice({
              key: `slot-${n}-${variant.id}`,
              action: 'slot',
              data: { slot: n, variant: variant.id },
              label: module.title,
              meta: variant.available ? `+${this.formatMoney(variant.price)}` : this.strings.outOfStock,
              checked: current === variant.id,
              disabled: !variant.available,
            });
          })
          .join('');
        return `
          <div class="cfg-group">
            <p class="cfg-group__name">${escapeHtml(colour)}</p>
            <div class="cfg-choices" role="radiogroup">${choices}</div>
          </div>`;
      })
      .join('');

    const body = `<div class="cfg-choices" role="radiogroup">${passive}</div>${groups}`;
    return this.stepShell(key, title, step.description, picked, body);
  }

  // Extras: any quantity of each variant, grouped by product
  renderExtras(step) {
    return step.products
      .map((product) => {
        const single = product.variants.length === 1 && product.variants[0].title === product.title;
        const rows = product.variants
          .map((variant) => {
            const quantity = this.state.extras[variant.id] ?? 0;
            const label = variant.title;
            const meta = variant.available
              ? `+${this.formatMoney(variant.price)} ${this.strings.each}`
              : this.strings.outOfStock;
            const classes = [
              'cfg-quantity',
              quantity > 0 ? 'cfg-quantity--active' : '',
              variant.available ? '' : 'cfg-quantity--unavailable',
            ].join(' ');
            return `
              <li class="${classes}">
                <span class="cfg-quantity__text">
                  <span class="cfg-choice__label">${escapeHtml(label)}</span>
                  <span class="cfg-choice__meta">${escapeHtml(meta)}</span>
                </span>
                <span class="cfg-quantity__controls" role="group" aria-label="${escapeHtml(label)}">
                  <button type="button" class="cfg-quantity__button" data-action="extra" data-variant="${variant.id}" data-delta="-1" data-key="extra-${variant.id}-minus" aria-label="${escapeHtml(interpolate(this.strings.decrease, { item: label }))}" ${quantity === 0 ? 'disabled' : ''}>−</button>
                  <span class="cfg-quantity__count" aria-live="polite">${quantity}</span>
                  <button type="button" class="cfg-quantity__button" data-action="extra" data-variant="${variant.id}" data-delta="1" data-key="extra-${variant.id}-plus" aria-label="${escapeHtml(interpolate(this.strings.increase, { item: label }))}" ${!variant.available || quantity >= MAX_EXTRA_QUANTITY ? 'disabled' : ''}>+</button>
                </span>
              </li>`;
          })
          .join('');
        return `
          <div class="cfg-group">
            ${single ? '' : `<p class="cfg-group__name">${escapeHtml(product.title)}</p>`}
            <ul class="cfg-quantities">${rows}</ul>
          </div>`;
      })
      .join('');
  }

  pickedExtras(step) {
    const picked = step.products.flatMap((product) =>
      product.variants
        .filter((variant) => this.state.extras[variant.id] > 0)
        .map((variant) => {
          const quantity = this.state.extras[variant.id];
          return `${variant.title}${quantity > 1 ? ` × ${quantity}` : ''}`;
        })
    );
    return picked.length ? picked.join(', ') : this.strings.none;
  }

  renderSummary(summary = this.summarize()) {
    const s = this.strings;
    const lines = summary.lines
      .map(
        (line) => `
          <li>
            <span>${escapeHtml(line.label)}${line.quantity > 1 ? ` × ${line.quantity}` : ''}</span>
            <span>${line.amount !== null ? this.formatMoney(line.amount) : '—'}</span>
          </li>`
      )
      .join('');

    let status = '';
    if (this.status === 'added') {
      status = `<p class="cfg-summary__status" role="status">${escapeHtml(s.added)} <a href="${escapeHtml(this.data.cartUrl)}">${escapeHtml(s.viewCart)}</a></p>`;
    } else if (this.status === 'unavailable' || this.status === 'error') {
      const message = this.errorDetail ?? (this.status === 'unavailable' ? s.unavailable : s.error);
      status = `<p class="cfg-summary__error" role="alert">${escapeHtml(message)}</p>`;
    }

    const wasOpen = this.summaryEl.querySelector('details')?.open;
    this.summaryEl.innerHTML = `
      <details class="cfg-summary__lines" ${wasOpen ? 'open' : ''}>
        <summary>${escapeHtml(s.summary)}</summary>
        <ul>${lines}</ul>
      </details>
      <div class="cfg-summary__total">
        <span class="cfg-summary__total-label">${escapeHtml(s.total)}</span>
        <span class="cfg-summary__total-value" data-testid="configurator-total">${this.formatMoney(summary.total)}</span>
      </div>
      <p class="cfg-summary__note">${escapeHtml(s.shipping)}</p>
      <button type="button" class="flux-btn flux-btn--primary flux-btn--block" data-action="add" ${this.pending || !summary.purchasable ? 'disabled' : ''}>
        ${escapeHtml(this.pending ? s.adding : summary.purchasable ? s.addToCart : s.outOfStock)}
      </button>
      ${status}`;
  }
}

if (!customElements.get('flux-configurator')) customElements.define('flux-configurator', FluxConfigurator);
